import React, { useState, useEffect, useMemo, useRef } from "react";
import CAMHeader from "../../../components/Header/CAMHeader";
import CAMDrawer from "../../../components/LeftSideBar/CAMDrawer";
import InvestigatorContainer from "../../../components/contsiners/InvestigatorContainer";
import TabsNav from "../common/ProcessNav";
import { Box, InputBase, Button, CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "../../../assets/filter.svg";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AuditTrailTable from "./AuditTrailTable";
import Pagination from "../../../components/Pagination/index";
import Loader from "../../../components/Loader/loader";
import SnackBar from "../../../components/Snackbars/CustomizedSnackbars";
import { useDispatch, useSelector } from "react-redux";
import * as uop from "../../../redux/audit_config/actions";
import debounce from "lodash.debounce";
import { useNavigate } from "react-router-dom";
import "./auditTrail.scss";
import FiltersPopup from "./filtersPopup";
import CustomizedSnackbars from "../../../components/Snackbars/CustomizedSnackbars";

const AuditTrailList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportCancelledRef = useRef(false);

  // Sorting state
  // null means original API order
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const page_size = 50;

  const rowData = useSelector(
    (state) => state?.auditConfig?.configAuditTrailList,
  );

  const filterOptions = useSelector(
    (state) => state?.auditConfig?.configAuditTrailFilters,
  );

  const apiStatus = useSelector(
    (state) => state?.auditConfig?.configAuditTrailList,
  );

  const [message, setMessage] = useState("");
  const [err, setErr] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState();

  const [filters, setFilters] = useState({
    entity_id: "",
    entity_type: "",
    event_name: "",
    created_by: "",
    from_date: "",
    to_date: "",
    status: "",
  });

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const debouncedSetValue = useMemo(
    () =>
      debounce((val) => {
        setDebouncedSearch(val);
      }, 700),
    [],
  );

  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    debouncedSetValue(e.target.value);
  };

  const handleChange = (field, value) => {
    if (field === "entity_id" && value !== "") {
      const selectedEntity = filterOptions?.data?.entities?.find(
        (entity) => entity.entity_id === value,
      );

      if (selectedEntity) {
        setFilters((prev) => ({
          ...prev,
          entity_id: value,
          entity_type: selectedEntity.entity_type,
        }));

        return;
      }
    }

    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReset = () => {
    setFilters({
      entity_id: "",
      entity_type: "",
      event_name: "",
      created_by: "",
      from_date: "",
      to_date: "",
      status: "",
    });

    // Reset sorting also when filters are reset
    setSortBy(null);
    setSortOrder(null);

    dispatch(
      uop.getConfigAuditTrailList({
        page: 1,
        page_size,
      }),
    );
  };

  // Sorting Handler
  //
  // First click  -> ASC
  // Second click -> DESC
  // Third click  -> Original API order
  const handleSort = (columnKey) => {
    if (sortBy !== columnKey) {
      // First click on a column
      setSortBy(columnKey);
      setSortOrder("ASC");
    } else if (sortOrder === "ASC") {
      // Second click on same column
      setSortOrder("DESC");
    } else {
      // Third click on same column
      // Restore original API order
      setSortBy(null);
      setSortOrder(null);
    }
  };

  useEffect(() => {
    dispatch(uop.clearConfigAuditTrailList());

    return () => {
      exportCancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (apiStatus?.apiError === true) {
      setMessage(apiStatus?.message || "Something went wrong");
      setErr(true);
    } else {
      setMessage("");
      setErr(false);
    }
  }, [apiStatus]);

  useEffect(() => {
    dispatch(uop.getConfigurationFiltersList());
  }, [dispatch]);

  useEffect(() => {
    setPage(1);

    dispatch(
      uop.getConfigAuditTrailList({
        page: 1,
        page_size,
        query: search,
      }),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleGenerate = () => {
    setPage(1);

    const formattedFilters = {
      ...filters,
      from_date: formatDate(filters.from_date),
      to_date: formatDate(filters.to_date),
    };

    dispatch(
      uop.getConfigAuditTrailList({
        page: 1,
        page_size,
        ...formattedFilters,
      }),
    );
  };

  const handlePageChange = (event, value) => {
    setPage(value);

    const formattedFilters = {
      ...filters,
      from_date: formatDate(filters.from_date),
      to_date: formatDate(filters.to_date),
    };

    dispatch(
      uop.getConfigAuditTrailList({
        page: value,
        page_size,
        ...formattedFilters,
      }),
    );
  };

  const handleExport = async () => {
    if (isExporting) return;

    setIsExporting(true);
    exportCancelledRef.current = false;

    const formattedFilters = {
      ...filters,
      from_date: formatDate(filters.from_date),
      to_date: formatDate(filters.to_date),
    };

    const cleanParams = {};
    if (search) cleanParams.query = search;
    Object.entries(formattedFilters).forEach(([key, val]) => {
      if (val !== "" && val !== undefined && val !== null) {
        cleanParams[key] = val;
      }
    });

    try {
      const exportRes = await dispatch(
        uop.exportConfigAuditTrailList(cleanParams),
      );

      const taskId =
        exportRes?.data?.task_id ||
        exportRes?.data?.data?.task_id ||
        exportRes?.task_id;

      if (!taskId) {
        throw new Error(
          exportRes?.data?.message ||
            exportRes?.message ||
            "Unable to initiate export task.",
        );
      }

      const MAX_RETRIES = 40;
      const RETRY_INTERVAL = 3000;

      for (let poll = 0; poll < MAX_RETRIES; poll += 1) {
        if (exportCancelledRef.current) return;

        if (poll > 0) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_INTERVAL));
        }

        if (exportCancelledRef.current) return;

        const statusRes = await dispatch(
          uop.getConfigAuditTrailExportStatus(taskId),
        );

        const statusData = statusRes?.data?.data || statusRes?.data || {};
        const status = String(
          statusData.task_status ||
            statusData.status ||
            statusRes?.data?.task_status ||
            "",
        ).toUpperCase();

        if (["SUCCESS", "COMPLETED", "COMPLETE"].includes(status)) {
          const downloadUrl =
            statusData.download_url ||
            statusData.file_url ||
            statusRes?.data?.download_url;

          if (downloadUrl) {
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = `audit_trail_export_${Date.now()}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }

          const apiMsg = statusData.message || statusRes?.data?.message;
          const isGenericMsg =
            !apiMsg ||
            ["ok", "success", "true"].includes(String(apiMsg).trim().toLowerCase());

          setSnack({
            open: true,
            message: isGenericMsg
              ? "Audit trail exported successfully."
              : apiMsg,
            severity: "success",
          });
          return;
        }

        if (["FAILURE", "FAILED", "REVOKED", "CANCELLED"].includes(status)) {
          throw new Error(
            statusData.message ||
              statusRes?.data?.message ||
              "Audit trail export failed.",
          );
        }
      }

      throw new Error("Audit trail export timed out. Please try again.");
    } catch (error) {
      if (!exportCancelledRef.current) {
        setSnack({
          open: true,
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Error exporting audit trail.",
          severity: "error",
        });
      }
    } finally {
      if (!exportCancelledRef.current) {
        setIsExporting(false);
      }
    }
  };

  const handleRowClick = (row) => {
    navigate(`/version-history`, {
      state: {
        entityType: row?.entity_type,
        entityName: row?.entity_name,
        entityId: row?.entity_id,
        auditId: row?.audit_id,
      },
    });

    dispatch(uop.clearConfigurationVersions());
  };

  return (
    <>
      <CAMHeader />

      <CAMDrawer />

      <InvestigatorContainer>
        <TabsNav />

        <Box className="audit-trail-header">
          <span className="audit-trail-title">Audit Trail</span>

          <Box className="audit-trail-header-actions">
            <Box className="audit-trail-search">
              <SearchIcon
                fontSize="small"
                className="audit-trail-search-icon"
              />

              <InputBase
                placeholder="Search"
                value={search}
                onChange={handleSearch}
                fullWidth
                size="small"
              />
            </Box>

            <Button
              variant="text"
              className="audit-trail-filter-btn"
              startIcon={<img src={FilterListIcon} alt="Filter" />}
              onClick={() => setFilterOpen(true)}
            >
              Filter
            </Button>

            <Button
              variant="contained"
              size="small"
              className="audit-trail-export-btn"
              startIcon={
                isExporting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FileDownloadOutlinedIcon fontSize="small" />
                )
              }
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? "Exporting..." : "Export"}
            </Button>
          </Box>
        </Box>

        <FiltersPopup
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          filters={filters}
          onChange={handleChange}
          onReset={handleReset}
          onGenerate={handleGenerate}
          isStatusVisible={true}
          data={rowData || []}
          filterOptions={filterOptions?.data || {}}
        />

        <Box width="100%" className="audit-trail-table-wrap">
          <AuditTrailTable
            data={rowData}
            onRowClick={handleRowClick}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />

          {rowData?.loading && <Loader />}

          <Pagination
            count={rowData?.data?.total / page_size}
            page={page}
            onChange={handlePageChange}
          />
        </Box>

        {snack?.open && (
          <SnackBar
            className="popup"
            handleClose={() =>
              setSnack({
                open: false,
                message: "",
                severity: "",
              })
            }
            severity={snack.severity}
            state={snack?.open}
            text={snack?.message}
            autoHideDuration={3000}
          />
        )}

        {err && (
          <CustomizedSnackbars
            severity="error"
            state={err}
            text={message}
            handleClose={() => setErr(false)}
            autoHideDuration={3000}
          />
        )}
      </InvestigatorContainer>
    </>
  );
};

export default AuditTrailList;